import { Badge, badgeClasses } from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import Fab from '@mui/material/Fab'
import Icon from '@mui/material/Icon'
import { styled } from '@mui/material/styles'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { find, pathOr, propEq } from 'ramda'
import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom-v5-compat'

import { createTransition } from '@/app/component/atom/right-drawer'
import Loading from '@/app/component/guard/loading'
import { DEFAULT_SENDER_ID_VALUE } from '@/app/definitions'
import { usePrevious } from '@/app/helpers'
import { formatPhoneNumber } from '@/app/module/conversations/format-helpers'
import { ContactType, SenderIdType } from '@/app/module/conversations/types'
import { SendWhatsappMessagePayload } from '@/app/module/logs/types'
import { FileType, FileUploadType, MessageType, PersonalizationType, SMSLog, WhatsAppLog } from '@/app/types'

import Contacts from './contacts'
import ConversationDrawer, { CONVERSATION_DRAWER_WIDTH } from './conversation-drawer'
import History from './history'
import Send from './send'

type ContactHistoryItem = Pick<SMSLog | WhatsAppLog, 'type'> & {
  direction?: string
  recipient?: string
  sender?: string
}

type Props = {
  contactId: number
  contactInfo: ContactType
  conversations: ContactType[]
  initialized: boolean
  loading: boolean
  nextPage: string
  orgId: number
  personalizationList: PersonalizationType
  smsSenderIds: SenderIdType[]
  token: string
  whatsappSenderIds: SenderIdType[]

  getContactData: (contactId: number, nextpage?: string) => void
  getContactInfo: (activeContactId: number) => void
  getContacts: (payload: { nextPage?: string; search?: string }) => Promise<unknown>
  getCustomFields: (payload: { orgId: number; token: string }) => Promise<unknown>
  getFilesItem: (payload: { name: string; orgId: number; token: string }) => Promise<FileType>
  getSenderIdList: (payload: { orgId: number; token: string }) => Promise<unknown>
  onMicAccessError: () => void
  readAllContactMessages: (activeContactId: number) => void
  saveWhatsappFile: (payload: { item: FileUploadType; orgId: number; token: string }) => Promise<FileType | Error>
  sendWhatsappMessage: (payload: SendWhatsappMessagePayload & { orgId: number; token: string }) => Promise<unknown>
  subscribeLogs: () => void
  unsubscribeLogs: () => void
}

const emptyContact: ContactType = {
  contactId: 0,
  firstName: '',
  lastName: '',
  fullPhoneNumber: '',
  created: '',
  groups: [],
  subscriptions: [],
  countryCode: '',
  history: [],
  customFields: [],
  segments: [],
  unreadCount: 0,
  logsDone: false,
}

let globalContact: ContactType

const Conversations: React.FC<Props> = (props) => {
  const {
    contactId: activeContactId,
    contactInfo,
    conversations: contacts,
    initialized,
    loading,
    nextPage,
    orgId,
    personalizationList,
    smsSenderIds,
    token,
    whatsappSenderIds,

    getContactData,
    getContactInfo,
    getContacts,
    getCustomFields,
    getFilesItem,
    getSenderIdList,
    onMicAccessError,
    readAllContactMessages,
    saveWhatsappFile,
    sendWhatsappMessage,
    subscribeLogs,
    unsubscribeLogs,
  } = props

  const navigate = useNavigate()
  const location = useLocation()
  const wrapper = React.useRef<HTMLDivElement>(null)
  const [scrolledHistory, setScrolledHistory] = React.useState(false)
  const [infoView, setInfoView] = React.useState(true)
  const [searchTerm, setSearchTerm] = React.useState('')
  const [changed, setTextChange] = React.useState(false)
  const [messageType, setMessageType] = React.useState<MessageType>(MessageType.SMS)
  const [selectedSenderId, setSelectedSenderId] = React.useState(DEFAULT_SENDER_ID_VALUE)

  const sendRef = React.useRef<HTMLDivElement>(null)

  const setActiveContactId = React.useCallback(
    (contactId: number) => {
      navigate(`/conversations?contactId=${contactId}`)
    },
    [navigate],
  )

  React.useEffect(() => {
    if (location.pathname !== '/conversations') {
      unsubscribeLogs()
    }
  }, [location.pathname, unsubscribeLogs])

  React.useEffect(() => {
    if (token && orgId) {
      getContacts({})
      getCustomFields({ token, orgId })
      getSenderIdList({ token, orgId })
      subscribeLogs()
    }
  }, [orgId, token, getContacts, getCustomFields, getSenderIdList, subscribeLogs])

  React.useEffect(() => {
    if (initialized && !loading && !activeContactId) {
      setActiveContactId(pathOr(0, [0, 'contactId'], contacts))
    }
  }, [activeContactId, contacts, initialized, loading, setActiveContactId])

  React.useEffect(() => {
    if (initialized && activeContactId) {
      getContactInfo(activeContactId)
      getContactData(activeContactId)
    }
  }, [activeContactId, initialized, getContactData, getContactInfo])

  React.useEffect(() => {
    const delay = setTimeout(() => {
      if (changed) {
        getContacts({ search: searchTerm, nextPage: '' })
      }
    }, 500)
    return () => clearTimeout(delay)
  }, [changed, searchTerm, getContacts])

  const saveFile = (file: FileUploadType): Promise<FileType | Error> => {
    if (token && orgId) {
      return saveWhatsappFile({
        orgId,
        token,
        item: file,
      })
    }
    return Promise.resolve(new Error('Missing token or orgId'))
  }

  const activeContact: ContactType = React.useMemo(
    () => find(propEq('contactId', activeContactId), contacts) || emptyContact,
    [activeContactId, contacts],
  )

  const prevContactId = usePrevious(activeContactId)
  React.useEffect(() => {
    // exit if no active contact or all logs initially loaded, unless contact changed
    if (!activeContactId || (activeContact.logsDone && activeContactId === prevContactId)) {
      return
    }

    const lastMessageType = getLastMessageType(activeContact.history[0])
    setMessageType(lastMessageType)
    setSelectedSenderId(resolveSenderId(lastMessageType, activeContact.history, smsSenderIds, whatsappSenderIds))
  }, [activeContactId, activeContact.history, activeContact.logsDone, prevContactId, smsSenderIds, whatsappSenderIds])

  const scrollToBottom = React.useCallback(() => {
    if (wrapper && wrapper.current) {
      setTimeout(() => {
        if (wrapper && wrapper.current) {
          wrapper.current.scrollTop = wrapper.current.scrollHeight
          setScrolledHistory(false)
        }
      }, 100)
      if (activeContact.unreadCount > 0) {
        readAllContactMessages(activeContactId)
      }
    }
  }, [activeContact.unreadCount, activeContactId, readAllContactMessages])

  const handleGetFile = React.useCallback(
    (filename: string) => getFilesItem({ orgId, token, name: filename }),
    [orgId, token, getFilesItem],
  )

  const handleHistoryLoadMore = React.useCallback(() => {
    if (!activeContact?.nextPage) {
      return
    }
    getContactData(activeContact.contactId, activeContact.nextPage)
  }, [activeContact, getContactData])

  // biome-ignore lint/correctness/useExhaustiveDependencies: scrollToBottom on activeContact change
  React.useEffect(() => {
    scrollToBottom()
  }, [activeContact.contactId, scrollToBottom])

  // biome-ignore lint/correctness/useExhaustiveDependencies: scrollToBottom on activeContact.history change if not scrolled
  React.useEffect(() => {
    if (!scrolledHistory) {
      scrollToBottom()
    }
  }, [activeContact.history, scrolledHistory, scrollToBottom])

  globalContact = activeContact
  const handleScroll = React.useCallback(() => {
    if (wrapper && wrapper.current) {
      const bottom =
        Math.ceil(wrapper.current.offsetHeight + wrapper.current.scrollTop) >= wrapper.current.scrollHeight - 10
      setScrolledHistory(!bottom)
      if (bottom && globalContact.contactId && globalContact.unreadCount > 0) {
        readAllContactMessages(globalContact.contactId)
      }
    }
  }, [readAllContactMessages])

  React.useEffect(() => {
    const { current } = wrapper
    current?.addEventListener('scroll', handleScroll)
    return () => {
      current?.removeEventListener('scroll', handleScroll)
    }
  }, [handleScroll])

  const handleMessageTypeChange = React.useCallback(
    (type: MessageType) => {
      setMessageType(type)

      setSelectedSenderId(resolveSenderId(type, activeContact.history, smsSenderIds, whatsappSenderIds))
    },
    [activeContact.history, smsSenderIds, whatsappSenderIds],
  )

  return (
    <>
      {initialized ? (
        <Loading isLoading={loading}>
          <ConversationsWrapper infoView={infoView}>
            <Scrollable>
              <Toolbar>
                <ToolbarSection />
                <TitleCenter variant="h6">Recent Conversations</TitleCenter>
                <ToolbarRightSection />
              </Toolbar>
              <ContactList>
                <Tooltip title="Search conversations using contact fields">
                  <ConversationsSearch>
                    <TextField
                      id="conversations-search-input"
                      size="small"
                      variant="outlined"
                      placeholder="Search conversations"
                      value={searchTerm}
                      onChange={(e) => {
                        if (!changed) {
                          setTextChange(true)
                        }
                        setSearchTerm(e.currentTarget.value)
                      }}
                    />
                  </ConversationsSearch>
                </Tooltip>
                <Contacts
                  loadMore={() => getContacts({ nextPage, search: searchTerm })}
                  nextPage={nextPage}
                  contacts={contacts}
                  activeContact={activeContact}
                  setActiveContact={setActiveContactId}
                />
              </ContactList>
            </Scrollable>
            {contacts.length > 0 || searchTerm || loading ? (
              <ConversationWrapper>
                <Toolbar>
                  <ToolbarSection>
                    <Typography>
                      {formatPhoneNumber(
                        activeContact.contactId,
                        activeContact.countryCode,
                        activeContact.fullPhoneNumber,
                      )}
                    </Typography>
                  </ToolbarSection>
                  <div>
                    {(activeContact.firstName || activeContact.lastName) && (
                      <TitleCenter variant="h6">
                        {activeContact.firstName} {activeContact.lastName}
                      </TitleCenter>
                    )}
                  </div>
                  <ToolbarRightSection />
                </Toolbar>
                <ConversationsCont ref={wrapper}>
                  <History activeContact={activeContact} getFile={handleGetFile} onLoadMore={handleHistoryLoadMore} />
                  {scrolledHistory && (
                    <ScrollDownBtn>
                      <NewMessageBadge
                        badgeContent={activeContact.unreadCount}
                        invisible={!activeContact.unreadCount}
                        color="primary"
                      >
                        <Fab size="small" color="default" aria-label="scroll-down" onClick={scrollToBottom}>
                          <Icon>expand_more</Icon>
                        </Fab>
                      </NewMessageBadge>
                    </ScrollDownBtn>
                  )}
                </ConversationsCont>
                <Send
                  contactId={activeContactId}
                  disabled={false}
                  messageType={messageType}
                  orgId={orgId}
                  personalizationList={personalizationList}
                  selectedSenderId={selectedSenderId}
                  token={token}
                  sendRef={sendRef}
                  smsSenderIds={smsSenderIds}
                  whatsappSenderIds={whatsappSenderIds}
                  getFile={handleGetFile}
                  onMessageTypeChange={handleMessageTypeChange}
                  onMicAccessError={onMicAccessError}
                  onSenderIdSelect={setSelectedSenderId}
                  saveFile={saveFile}
                  sendWhatsappMessage={sendWhatsappMessage}
                  setScrolledHistory={setScrolledHistory}
                />
              </ConversationWrapper>
            ) : (
              <NoDataWrap>
                <Typography variant="h5">{initialized ? "You don't have any messages yet" : 'Loading...'}</Typography>
              </NoDataWrap>
            )}
            <ConversationDrawer
              open={infoView}
              contactInfo={contactInfo}
              loading={loading}
              onClose={() => setInfoView(false)}
              onOpen={() => setInfoView(true)}
            />
          </ConversationsWrapper>
        </Loading>
      ) : (
        <div
          style={{
            padding: '60px',
            width: '100%',
            textAlign: 'center',
          }}
        >
          <CircularProgress />
        </div>
      )}
    </>
  )
}

export const getLastMessageType = (lastMessage?: { type: MessageType }) => {
  if (lastMessage?.type === MessageType.WhatsApp) {
    return MessageType.WhatsApp
  }

  return MessageType.SMS
}

const getLastSenderId = (history: ContactHistoryItem[], messageType: MessageType) => {
  const lastMessage = history.find((msg) => msg.type === messageType)

  if (!lastMessage) {
    return ''
  }

  return lastMessage.direction === 'egress' ? lastMessage.sender || '' : lastMessage.recipient || ''
}

export const resolveSenderId = (
  messageType: MessageType,
  history: ContactHistoryItem[],
  smsSenderIds: SenderIdType[],
  whatsappSenderIds: SenderIdType[],
  fallbackId = DEFAULT_SENDER_ID_VALUE,
) => {
  const lastSenderId = getLastSenderId(history, messageType)
  if (!lastSenderId) {
    return fallbackId
  }

  const senderIds = messageType === MessageType.WhatsApp ? whatsappSenderIds : smsSenderIds
  const senderIdExists = senderIds.some((senderId) => senderId.value === lastSenderId)
  return senderIdExists ? lastSenderId : fallbackId
}

const getGridTemplateColumns = (infoView: boolean, width = CONVERSATION_DRAWER_WIDTH) =>
  infoView ? `${width}px calc(100vw - ${width * 2}px) ${width}px` : `${width}px calc(100vw - ${width}px - 20px) 20px`

const ConversationsWrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'infoView',
})<{
  infoView: boolean
}>(({ theme, infoView }) => ({
  display: 'grid',
  width: '100%',
  height: 'calc(100vh - 64px)',
  background: theme.palette.background.default,
  gridTemplateRows: '60px auto 60px',
  gridTemplateAreas: `"toolbar toolbar toolbar"
    "lSidebar main rSidebar"
    "lSidebar sendMessage rSidebar"`,
  gridTemplateColumns: getGridTemplateColumns(infoView, CONVERSATION_DRAWER_WIDTH),
  transition: createTransition('grid-template-columns', theme, infoView),
}))

const TitleCenter = styled(Typography)(() => ({
  textAlign: 'center',
}))

const Scrollable = styled('div')(({ theme }) => ({
  background: theme.palette.background.default,
  gridRowStart: 1,
  gridRowEnd: 'span 3',
  position: 'relative',
  borderRight: `1px solid ${theme.palette.border.main}`,
}))

const ConversationWrapper = styled(Scrollable)(() => ({
  display: 'flex',
  flexDirection: 'column',
  height: 'calc(100vh - 64px)',
}))

const Toolbar = styled('div')(({ theme }) => ({
  gridArea: 'toolbar',
  border: 'none',
  backgroundColor: theme.palette.highlight.light,
  display: 'flex',
  alignItems: 'center',
  fontWeight: 500,
  position: 'sticky',
  top: 0,
  borderBottom: `1px solid ${theme.palette.border.main}`,
  zIndex: 999,
  minHeight: '51px',
}))

const ToolbarSection = styled('div')(() => ({
  flex: '1 1',
  padding: '10px',
  display: 'flex',
}))

const ToolbarRightSection = styled(ToolbarSection)(() => ({
  flexDirection: 'row-reverse',
}))

const ContactList = styled('div')(() => ({
  gridArea: 'lSidebar',
  overflowY: 'scroll',
  height: 'calc(100vh - 115px)',
}))

const ConversationsCont = styled('div')(() => ({
  gridArea: 'main',
  position: 'relative',
  flex: 1,
  overflowY: 'scroll',
}))

const ConversationsSearch = styled('div')(({ theme }) => ({
  padding: '10px',
  display: 'flex',
  flexDirection: 'column',
  borderBottom: `1px solid ${theme.palette.border.main}`,
}))

const NoDataWrap = styled('div')(() => ({
  display: 'flex',
  width: '100%',
  height: '100%',
  justifyContent: 'center',
  alignItems: 'center',
  paddingTop: '140px',
}))

const ScrollDownBtn = styled('div')(() => ({
  position: 'sticky',
  bottom: 20,
  paddingRight: 4,
  textAlign: 'right',
}))

const NewMessageBadge = styled(Badge)(() => ({
  [`& .${badgeClasses.badge}`]: {
    top: 5,
    right: 35,
    fontSize: 11,
    height: 17,
    minWidth: 17,
  },
}))

export default Conversations

export type { Props as ConversationsProps }
